import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { useRandomId } from 'js/utils/hooks/useRandomId';

import { Errors } from 'js/design_system/forms/Errors';

import 'app/assets/stylesheets/design_system/form.scss';
import styled from 'astroturf/react';

export type RadioButtonGroupProps = {
  /** The input's label. Shown at the top of the component. */
  label?: ReactNode;
  /** The input's description. Shown directly underneath the label. */
  description?: ReactNode;
  /** The options available */
  options: string[];
  /** The initial value, should be a member of options */
  value?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** The errors to show beneath the field */
  errors?: ReactNode[];
  /** Whether to decorate with error styling */
  hasError?: boolean;
  /** Use the small button variant (default=false) */
  small?: boolean;
  /** Change event for the selection */
  onChange?: (selected: string | undefined) => void;
};

/**
 * # RadioButtonGroup
 * A component wrapping a group of buttons that act as radio buttons.
 * Supports labels, descriptions, and error messages.
 *
 * Props:
 * - `label`: The input's label. Shown at the top of the component.
 * - `description`: The input's description. Shown directly underneath the label.
 * - `options`: The options available.
 * - `value`: The initial value, should be a member of options if given.
 * - `disabled`: Whether the input is disabled
 * - `hasError`: Whether to decorate the input with error styling
 * - `errors`: The errors to show beneath the input
 * - `small`: Use the small button variant
 * - `onChange`: Change event for the input
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { RadioButtonGroup } from 'js/design_system/forms/RadioButtonGroup';
 *
 * <RadioButtonGroup label="Select a choice" onSelect={onSelect} options={
 *   ['Choice A', 'Choice B', 'Choice C']
 * } onChange={(selected: string) => oneof Choice A|Choice B|Choice C|undefined}>
 * ```
 *
 */
export const RadioButtonGroup = ({
  label,
  description,
  options,
  value,
  disabled,
  errors = [],
  hasError = false,
  small = false,
  onChange,
}: RadioButtonGroupProps) => {
  // Random ID for ARIA attributes
  const id = useRandomId();

  const containerClasses = classNames('ds2-5-radio-button-group--container');

  const wrapperClasses = classNames('ds2-5-radio-button-group--wrapper', {
    'ds2-5-radio-button-group--disabled': disabled,
  });

  const labelClasses = label
    ? classNames('ds2-5-form--label', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const descriptionClasses = description
    ? classNames('ds2-5-form--desc', {
        'ds2-5-form--label-disabled': disabled,
      })
    : undefined;

  const [selection, setSelection] = useState<string | undefined>(value);

  const onClick = (selected: string) => {
    return () => {
      if (selected === selection) {
        setSelection(undefined);
        if (onChange) onChange(undefined);
      } else {
        setSelection(selected);
        if (onChange) onChange(selected);
      }
    };
  };

  return (
    <div
      role="group"
      className={containerClasses}
      aria-disabled={disabled}
      aria-labelledby={`${id}-label`}
    >
      <legend>
        {label && (
          <label
            htmlFor={`${id}-input`}
            id={`${id}-label`}
            className={labelClasses}
          >
            {label}
          </label>
        )}
        {description && (
          <aside id={`${id}-desc`} className={descriptionClasses}>
            {description}
          </aside>
        )}
      </legend>
      <div className={wrapperClasses}>
        {options.map((option) => (
          <RadioButton
            key={option}
            disabled={disabled}
            checked={selection === option}
            value={option}
            onClick={onClick(option)}
            small={small}
          />
        ))}
      </div>
      {!disabled && hasError && <Errors id={`${id}-errors`} errors={errors} />}
    </div>
  );
};

type RadioButtonProps = {
  value: string;
  onClick: () => void;
  disabled?: boolean;
  small?: boolean;
  checked?: boolean;
};

const PillButton = styled.button<RadioButtonProps>`
  @import 'app/assets/stylesheets/design_system/font';

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-xs-8) var(--spacing-xs-16);
  gap: 4px;

  background: var(--background-white);
  outline: 1px solid var(--stroke-light);
  border: 1px solid transparent;
  border-radius: var(--corner-radius-radius-round);
  color: var(--text-primary);

  flex: none;
  order: 0;
  flex-grow: 0;

  @include body__small;

  &.small {
    @include body__extra-small;
    padding: var(--spacing-xs-4) var(--spacing-xs-16);
  }

  &:hover {
    outline: 1px solid var(--button-secondary-stroke-hover);
    color: var(--text-hover);
  }

  &:active {
    outline: 1px solid var(--button-secondary-stroke-press);
    color: var(--text-press);
  }

  &.checked {
    outline: 1px solid var(--buttons-button-focus);
    border: 1px solid var(--buttons-button-focus);
  }

  &.disabled {
    outline: 1px solid var(--button-secondary-stroke-inactive);
    pointer-events: none;
    color: var(--buttons-button-text-inactive);
  }
`;

export const RadioButton = (props: RadioButtonProps) => {
  return <PillButton {...props}>{props.value}</PillButton>;
};
